import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store } from '@warp-drive/core';
import { reloadRecord } from './save-record.ts';
import type { MatchParticipantInsights } from '../types/secure-your-future';
import type { MatchParticipantExtension as MatchParticipant } from '../data/resources/match-participant.ext.ts';

const matchParticipantsUrl = (path: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'match-participants' },
    op: 'query',
    resourcePath: 'match-participants',
  };
  return `${buildBaseURL(opts)}/${path.replace(/^\/+/, '')}`;
};

/** PATCH /match-participants/:id/begin-enrollment */
export function beginEnrollmentRequest(id: string): RequestInfo {
  return {
    url: matchParticipantsUrl(`${id}/begin-enrollment`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/** PATCH /match-participants/:id/attempt-complete-enrollment */
export function activateMatchParticipantRequest(id: string): RequestInfo {
  return {
    url: matchParticipantsUrl(`${id}/attempt-complete-enrollment`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/** GET /match-participants/:id/insights */
export function getMatchParticipantInsightsRequest(id: string): RequestInfo {
  return {
    url: matchParticipantsUrl(`${id}/insights`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Begins enrollment and reloads the participant because the PATCH response is empty. The explicit
 * store supports owner-free resource callers.
 */
export async function beginMatchParticipantEnrollment(
  store: Store,
  participant: MatchParticipant
): Promise<MatchParticipant> {
  await store.request(beginEnrollmentRequest(participant.id as string));
  return reloadRecord(store, participant);
}

/**
 * Activate (attempt-complete-enrollment) a match participant and refresh the
 * record. Renamed from the legacy `activateMatchParticipant()` method to avoid
 * collision with the `activateMatchParticipantRequest` builder; see
 * `beginMatchParticipantEnrollment` for the rationale.
 *
 * The PATCH endpoint returns `head 204`, so we reload via the builder helper.
 */
export async function activateMatchParticipant(
  store: Store,
  participant: MatchParticipant
): Promise<MatchParticipant> {
  await store.request(activateMatchParticipantRequest(participant.id as string));
  return reloadRecord(store, participant);
}

/**
 * Fetches insights and assigns them to the participant; makes no request when hasDataForInsights is
 * false.
 */
export async function fetchMatchParticipantInsights(
  store: Store,
  participant: MatchParticipant
): Promise<void> {
  if (!participant.hasDataForInsights) {
    return;
  }
  const response = await store.request(
    getMatchParticipantInsightsRequest(participant.id as string)
  );
  participant.insights = (response.content as { data: MatchParticipantInsights }).data;
}
